import "./SolibriEditor.css";
import ContentPage from "./ContentPage";
import Highlighter from "./Highlighter";
import { ReactComponent as RightArrow } from "./static/icons/RightArrow.svg";
import { goToPage } from "./Slide";
import Button from "./Button";
import BuildingOrgVid from "./static/videos/BuildingOrg.mp4";
import RuleFiltersVid from "./static/videos/RuleFilters.mp4";
import NewCheckVid from "./static/videos/NewCheck.mp4";
import AnimatedDiv from "./AnimatedDiv";

function SolibriEditor() {
  return (
    <ContentPage>
      <AnimatedDiv>
        <h2>Solibri Inside: Editor</h2>

        <h3>Role</h3>
        <p>
          Product Design Lead | Product Designer| UX Researcher | UX Writer{" "}
        </p>

        <h3>What's Solibri Inside Editor?</h3>
        <p>
          Solibri Inside Editor is a browser-based tool for BIM
          Managers/Coordinators to set project requirements. The requirements
          can be instantly shared with the rest of the team. This helps ensure
          all models in the project meet the best QA standards and criteria.
        </p>
        <h3>Challenges</h3>
        <p>
          Editor is a huge project, so I'll tell you about a few design
          challenges I encountered and problems I solved among many many others.
        </p>
      </AnimatedDiv>
      <AnimatedDiv>
        <h3>Challenge: Creating a New Check</h3>
        <p>
          In Editor BIM Managers can create rules (the requirements the building
          models need to fulfil) and combine them into Checks. Then architects
          who use Solibri Inside Plugin in the design tool can run those checks.
        </p>
        <p>
          The Editor provides two ways of creating a check: by manually picking
          the rules, or by using labels. Creating a check based on labels
          automatically bundles all the rules with the same label into one
          check, so users don't need to do anything manually.{" "}
        </p>
        <Highlighter text="Unclear terminology and concepts " />
        <p>
          The check based on labels was originally called “SMART Check”. During
          user research we found out that the term didn't give any context, nor
          users understood what it means or how the check based on labels works.{" "}
        </p>
        <h3>Result</h3>
        <Highlighter text="Remove the term “SMART Check " />
        <p>
          Since users didn't understand what it meant we eliminated the term
          completely, and stared using the descriptive name — “Check based on
          labels”.
        </p>
        <Highlighter text="2. Usage and concepts explained " />
        <p>
          I introduced tooltips in the new check creation flow and added more
          description fields so that users understand the flow and concepts
          better.
        </p>
        <p>New Check user flow</p>
        <video
          className="videoCard"
          muted
          autoPlay
          loop
          width="100%"
          height="100%"
        >
          <source src={NewCheckVid} />
        </video>
      </AnimatedDiv>
      <AnimatedDiv>
        <h3>Challenge: Huge list of Rules</h3>
        <p>
          As I mentioned earlier, users can create rules (the requirements the
          building models need to fulfil) and combine them into Checks. Those
          rules are created for a specific use case and are shared among the
          users of an organisation. This means that a list of rules will become
          huge, which will make it difficult to use and manage. All the rules
          live in a separate page and are displayed in a table.
        </p>
        <h3>Result</h3>
        <Highlighter text="1. Allow filtering, sorting, and search " />
        <p>
          When there's a huge list of information how do you manage that? You
          sort and filter. And what could be even better and more efficient than
          sorting and filtering? Of course, a search field. So, we allow user to
          sort, filter and search the huge list of information.
        </p>
        <Highlighter text="2. Improve data visibility " />
        <p>
          After talking with potential users I've defined the information that
          had to be visible in the table. So, it's not just some info, but the
          one they actually need. We removed and replaced some table columns
          with the ones that had relevant information for the user.
        </p>
        <p>
          We replaced the IFC schema information column with the label column.
          Label column in the table is important to help users comprehend the
          concept of “Check based on labels” better. Filtering by IFC Schema is
          still possible by filtering.
        </p>
        <p>Rules page with rule filters</p>
        <video
          className="videoCard"
          muted
          autoPlay
          loop
          width="100%"
          height="100%"
        >
          <source src={RuleFiltersVid} />
        </video>
      </AnimatedDiv>
      <AnimatedDiv>
        <h3>Challenge: Creating organisation takes time</h3>
        <p>
          When user upgrades their account to Premium, they create their
          organisation where they can add all the other users. Unfortunately,
          because of the technical limitations creating the organisation
          structure and providing this data to the UI takes time (up to 5mins).{" "}
        </p>
        <p>
          The user has gone through the Upgrade flow and paid to have the
          Premium features, however they don't get the Premium experience
          instantly after the upgrade.{" "}
        </p>
        <h3>Result</h3>
        <Highlighter text="1. Sticky alert component " />
        <p>
          To inform the users that the Upgrade was successful and everything is
          on the right track - they just need to wait a little, I introduced the
          sticky alert component. It appears on every page of the Editor and
          can't be dismissed.{" "}
        </p>
        <Highlighter text="2. “Building your organisation” screen " />
        <p>
          Creating the organisation is a process. Users can click on the
          available UI elements and find themselves on a page that isn't
          complete. The Editor would be still working on fetching and displaying
          all the data.{" "}
        </p>
        <p>
          To eliminate the feeling that the information on a page is missing, or
          that the page is broken we introduced “Building your organisation”
          screen.{" "}
        </p>
        <p>Building your organisation screen</p>
        <video
          className="videoCard"
          muted
          autoPlay
          loop
          width="100%"
          height="100%"
        >
          <source src={BuildingOrgVid} />
        </video>
        <p>
          The things you've just read about are very few challenges we had
          during 1,5 years of working on Solibri Inside Editor. Technical
          limitations, time constraints, and complex domain had been with us
          throughout the whole journey.
        </p>
        <Button
          className="goToCaseButton"
          variant="complex"
          text="SEE SOLIBRI DESIGN SYSTEM"
          onClick={() => goToPage("/case1/designsystem")}
          icon={<RightArrow />}
        />
      </AnimatedDiv>
    </ContentPage>
  );
}

export default SolibriEditor;
